import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API, { createWS } from '../api';
import { useApp } from '../store';
import { cleanupAudio, initAudio, playBgm, playLoopSfx, playSfx, stopBgm } from '../audio';
import { useLang, LangToggle } from '../i18n';

const ROOM_CONFIG = {
  1: { name:'初级场', nameEn:'Beginner', carry:1000    },
  2: { name:'中级场', nameEn:'Intermediate', carry:10000   },
  3: { name:'高级场', nameEn:'Advanced', carry:1000000 },
};
const ROOM_STATUS_LABELS = {
  WAITING: '匹配中',
  READYING: '准备中',
  COUNTDOWN: '倒计时中',
  RUNNING: '进行中',
  SETTLING: '结算中',
};
const LEVERAGE = 1000;
const SPREAD   = 0.0002;
const PRICE_POINT_LIMIT = 800;

/* ── 跑马灯组件 ── */
function Marquee({ text, url }) {
  if (!text) return null;
  const inner = url
    ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{text}</a>
    : <span>{text}</span>;
  return (
    <div style={{
      height: 26, overflow: 'hidden', flexShrink: 0,
      background: 'rgba(46,127,232,0.10)',
      borderBottom: '1px solid rgba(46,127,232,0.15)',
      display: 'flex', alignItems: 'center',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0,
        animation: 'marqueeScroll 18s linear infinite',
        whiteSpace: 'nowrap', willChange: 'transform',
        fontSize: 12, color: '#8BA8D4', fontWeight: 500,
      }}>
        <span style={{ paddingRight: 80 }}>{inner}</span>
        <span style={{ paddingRight: 80 }}>{inner}</span>
      </div>
      <style>{`
        @keyframes marqueeScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

const LIME = '#2E7FE8'; const GOLD = '#F5C518';
const RED  = '#E8453C';

function appendPricePoint(prev, price, ts = Date.now()) {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return prev;
  const last = prev[prev.length - 1];
  const nextTs = last && ts <= last.ts ? last.ts + 1 : ts;
  const next = [...prev, { ts: nextTs, price: p }];
  return next.length > PRICE_POINT_LIMIT ? next.slice(-PRICE_POINT_LIMIT) : next;
}

function getRoomLabel(level) {
  const room = ROOM_CONFIG[Number(level)];
  return room?.name || `L${level}`;
}

function getStatusLabel(status) {
  return ROOM_STATUS_LABELS[status] || status || '--';
}

function isCurrentRoomMessage(messageRoomId, currentRoomId) {
  if (messageRoomId == null || currentRoomId == null) return false;
  return String(messageRoomId) === String(currentRoomId);
}

/* ── SFX ── */
const SFX = {
  click: () => playSfx('button-click', 0.9),
  open: () => playSfx('gamble-flip', 0.9),
  profit: () => playSfx('score-up', 0.75, 1.2),
  loss: () => playSfx('short-circuit', 0.4, 1.35),
  jackpot: () => playSfx('combo-win', 1.05),
  combo: (level = 1) => {
    const n = Math.max(1, Math.min(8, Number(level) || 1));
    playSfx(n >= 3 ? 'combo-win' : 'cluster-win', n >= 3 ? 1 : 0.8, Math.min(1.35, 0.92 + n * 0.05));
  },
  comboCash: () => playSfx('score-up', 0.85, 1.55),
  win: () => playSfx('gamble-win', 0.9),
  lose: () => playSfx('gamble-lose', 0.9),
  bust: () => playSfx('bomb-explode', 1),
  settleWin: () => playSfx('settle-win', 1),
  settleLose: () => {
    playSfx('gamble-lose', 0.9);
    setTimeout(() => playSfx('bomb-explode', 1), 120);
  },
};

/* ── K线图：黄绿渐变面积图（仿右图 ETH 风格）── */
/* ── 结算特效组件 ─────────────────────────────────────────────────── */
function SettleScreen({ won, netStr, settled, carryPts, LIME, RED, S, onRetry, onLobby, lang = 'zh', t = k => k }) {
  const cvRef = useRef(null);
  const shakeRef = useRef(null);

  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const W = cv.width  = window.innerWidth;
    const H = cv.height = window.innerHeight;
    const ctx = cv.getContext('2d');
    let anim;

    if (won) {
      // 金币粒子
      const coins = Array.from({length: 80}, () => ({
        x: Math.random() * W,
        y: -20 - Math.random() * 200,
        vy: 2 + Math.random() * 4,
        vx: (Math.random() - 0.5) * 3,
        r: 6 + Math.random() * 8,
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 0.2,
        hue: 45 + Math.random() * 20,   // 金黄色系
        alpha: 1,
      }));
      let frame = 0;
      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        frame++;
        for (const c of coins) {
          c.y  += c.vy;
          c.x  += c.vx;
          c.rot += c.rotV;
          c.vy += 0.12; // 重力
          if (frame > 80) c.alpha = Math.max(0, c.alpha - 0.012);
          ctx.save();
          ctx.globalAlpha = c.alpha;
          ctx.translate(c.x, c.y);
          ctx.rotate(c.rot);
          // 硬币椭圆（侧面旋转感）
          ctx.beginPath();
          ctx.ellipse(0, 0, c.r, c.r * Math.abs(Math.cos(c.rot * 3)) + 1, 0, 0, Math.PI * 2);
          ctx.fillStyle = `hsl(${c.hue},90%,55%)`;
          ctx.fill();
          ctx.strokeStyle = `hsl(${c.hue},90%,35%)`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.restore();
        }
        if (frame < 160) anim = requestAnimationFrame(draw);
        else ctx.clearRect(0, 0, W, H);
      };
      draw();
    } else {
      // 炸弹爆炸粒子 + 屏幕震动
      // 震动
      const el = shakeRef.current;
      if (el) {
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.animation = 'shake 0.5s ease';
      }
      // 爆炸粒子从屏幕中央喷出
      const cx = W / 2, cy = H / 2;
      const sparks = Array.from({length: 60}, () => {
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 10;
        return {
          x: cx, y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          r: 3 + Math.random() * 5,
          alpha: 1,
          color: Math.random() > 0.4 ? '#E8453C' : (Math.random() > 0.5 ? '#FF8800' : '#FFD700'),
        };
      });
      // 爆炸闪光
      ctx.fillStyle = 'rgba(232,69,60,0.35)';
      ctx.fillRect(0, 0, W, H);
      let frame2 = 0;
      const draw2 = () => {
        ctx.clearRect(0, 0, W, H);
        frame2++;
        for (const s of sparks) {
          s.x += s.vx;
          s.y += s.vy;
          s.vy += 0.3;
          s.alpha = Math.max(0, s.alpha - 0.022);
          ctx.save();
          ctx.globalAlpha = s.alpha;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = s.color;
          ctx.fill();
          ctx.restore();
        }
        if (frame2 < 80) anim = requestAnimationFrame(draw2);
        else ctx.clearRect(0, 0, W, H);
      };
      draw2();
    }
    return () => cancelAnimationFrame(anim);
  }, [won]);

  return (
    <div ref={shakeRef} style={{position:'relative',minHeight:'100vh',height:'100dvh',background:'#0B1E4A',
      display:'flex',alignItems:'center',justifyContent:'center',padding:20,overflow:'hidden'}}>
      <style>{S}{`
        @keyframes shake {
          0%   { transform: translate(0,0) rotate(0deg); }
          10%  { transform: translate(-8px,-6px) rotate(-2deg); }
          20%  { transform: translate(8px,6px) rotate(2deg); }
          30%  { transform: translate(-8px,4px) rotate(-1deg); }
          40%  { transform: translate(8px,-4px) rotate(1deg); }
          50%  { transform: translate(-6px,6px) rotate(-1deg); }
          60%  { transform: translate(6px,-2px) rotate(0deg); }
          70%  { transform: translate(-4px,4px) rotate(1deg); }
          80%  { transform: translate(4px,-2px) rotate(0deg); }
          90%  { transform: translate(-2px,2px) rotate(0deg); }
          100% { transform: translate(0,0) rotate(0deg); }
        }
      `}</style>

      {/* 全屏 canvas 特效层 */}
      <canvas ref={cvRef} style={{position:'fixed',top:0,left:0,width:'100%',height:'100%',
        pointerEvents:'none',zIndex:10}}/>

      <div style={{width:'100%',maxWidth:440,position:'relative',zIndex:20,overflowY:'auto',maxHeight:'100dvh',padding:'20px 0'}}>
        {/* WIN / OUT */}
        <div style={{position:'relative',textAlign:'center',marginBottom:8}}>
          <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',
            width:180,height:180,borderRadius:'50%',
            background:`radial-gradient(circle,${won?GOLD:RED}44 0%,transparent 70%)`,
            filter:'blur(30px)'}}/>
          <div style={{position:'relative',fontSize:72,fontWeight:900,color:won?GOLD:RED,lineHeight:1}}>
            {won?'WIN':'OUT'}
          </div>
        </div>

        {/* 本局结算数字 */}
        <div style={{textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:12,color:'#8BA8D4',letterSpacing:1,marginBottom:4}}>{t('settleTitle')}</div>
          <div style={{fontFamily:'Share Tech Mono',fontSize:34,fontWeight:900,color:won?GOLD:RED,lineHeight:1}}>
            {netStr} COIN
          </div>
        </div>

        {/* 所有玩家结算排行 */}
        {(settled.allResults||[]).length > 0 && (()=>{
          const E = settled.carryPtsVal || carryPts;
          const sorted = [...(settled.allResults||[])].sort((a,b)=>(b.settlePts||0)-(a.settlePts||0));
          const totalPool = sorted.reduce((s,r)=>s+(r.isBust?E:0),0);
          return (
            <div style={{background:'#0F2554',borderRadius:16,padding:'16px',marginBottom:16}}>
              <div style={{fontSize:11,color:'#8BA8D4',letterSpacing:1,marginBottom:10,textTransform:'uppercase'}}>{lang==='en'?'Round Leaderboard':'本局结算排行'}</div>
              {sorted.map((r,i)=>{
                const isMe = r.userId===settled.userId;
                const pts = r.isBust ? 0 : (r.finalPts||0);
                const settle = r.settlePts||0;
                const net = settle - E;
                return (
                  <div key={r.userId} style={{
                    display:'flex',alignItems:'center',gap:10,
                    padding:'10px 12px',borderRadius:10,marginBottom:6,
                    background: isMe ? 'rgba(245,197,24,0.1)' : 'rgba(46,127,232,0.07)',
                  }}>
                    <div style={{width:24,height:24,borderRadius:'50%',flexShrink:0,
                      background:i===0?'linear-gradient(180deg,#FFD700,#E6A800)':i===1?'#B0B8C1':'#CD7F32',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:12,fontWeight:900,color:'#000'}}>
                      {i+1}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:isMe?GOLD:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {r.nickname}{isMe?` (${t('settleYou')})` : ''}
                      </div>
                      <div style={{fontSize:11,color:'#8BA8D4',marginTop:2}}>
                        {r.isBust ? (lang==='en'?'Bust · Contributed to pool':'爆仓 · 贡献奖池') : `PTS: ${pts.toLocaleString()}`}
                      </div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontFamily:'Share Tech Mono',fontSize:16,fontWeight:900,
                        color: r.isBust ? RED : net>=0 ? GOLD : '#E8453C'}}>
                        {r.isBust ? '0' : settle.toLocaleString()}
                      </div>
                      <div style={{fontSize:11,color: net>=0?GOLD:RED}}>
                        {r.isBust?(lang==='en'?'Bust':'爆仓清零'):net>=0?`+${net.toLocaleString()}`:`${net.toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 结算逻辑说明 */}
              <div style={{marginTop:12,padding:'10px 12px',borderRadius:10,background:'rgba(255,255,255,0.04)'}}>
                <div style={{fontSize:11,color:'#8BA8D4',lineHeight:1.8}}>
                  <div>{lang==='en'?'Carry':'本局携带'}：<span style={{color:'#fff'}}>{E.toLocaleString()} COIN / {lang==='en'?'player':'人'}</span></div>
                  {totalPool>0&&<div>{lang==='en'?'Bust Pool':'爆仓奖池'}：<span style={{color:RED}}>{totalPool.toLocaleString()} COIN</span>{lang==='en'?' (busted players zeroed)':'（爆仓者积分清零）'}</div>}
                  <div>{lang==='en'?'Rules':'结算规则'}：{totalPool>0
                    ? (lang==='en'?'Survivors split bust pool by PTS weight + get carry back':'幸存者按游戏内PTS占比瓜分爆仓奖池 + 返还本金')
                    : (lang==='en'?"No busts — losers' profit redistributed to winners":'无人爆仓，输家亏损按比例分配给赢家')}
                  </div>
                  <div>{lang==='en'?'PTS is weight only, not withdrawable · COIN is settlement currency':'游戏内PTS仅作权重，不可带出 · COIN为实际结算货币'}</div>
                </div>
              </div>
            </div>
          );
        })()}

        <button className="btn-gold btn" style={{width:'100%',padding:'16px',fontSize:16}} onClick={onLobby}>{t('returnLobby')}</button>
      </div>
    </div>
  );
}

function FlipPrice({ value }) {
  const nextText = Number.isFinite(value) ? `$${value.toFixed(4)}` : '---';
  const [displayText, setDisplayText] = useState(nextText);
  const [prevText, setPrevText] = useState(nextText);
  const [flipId, setFlipId] = useState(0);

  useEffect(() => {
    if (nextText === displayText) return;
    setPrevText(displayText);
    setDisplayText(nextText);
    setFlipId(id => id + 1);
  }, [nextText, displayText]);

  const width = Math.max(displayText.length, prevText.length);
  const current = displayText.padStart(width, ' ');
  const previous = prevText.padStart(width, ' ');

  return (
    <div className="g-price-big" aria-label={displayText}>
      {current.split('').map((ch, i) => {
        const oldCh = previous[i] || ' ';
        const isDigit = /\d/.test(ch) || /\d/.test(oldCh);
        const changed = isDigit && oldCh !== ch;
        const cls = [
          'price-flip-cell',
          changed ? 'is-flipping' : '',
          ch === '.' ? 'is-dot' : '',
          ch === '$' ? 'is-symbol' : '',
        ].filter(Boolean).join(' ');
        return (
          <span key={`${flipId}-${i}-${ch}`} className={cls} aria-hidden="true">
            <span className="price-flip-static">{ch}</span>
            {changed && (
              <>
                <span className="price-flip-face price-flip-old">{oldCh}</span>
                <span className="price-flip-face price-flip-new">{ch}</span>
              </>
            )}
          </span>
        );
      })}
    </div>
  );
}

function ComboMeter({ streak = 0, best = 0, lang = 'zh' }) {
  const active = Math.max(0, Math.min(5, Number(streak) || 0));
  const bestCombo = Math.max(active, Math.min(5, Number(best) || 0));

  return (
    <div className={`combo-meter ${active > 0 ? 'is-live' : ''}`} aria-label={`Streak ${active}/5`}>
      <div className="combo-track">
        <span className="combo-label">{lang === 'en' ? 'STK' : '连胜'}</span>
        {[1,2,3,4,5].map(n => (
          <span key={n} className={`combo-cell ${n <= active ? 'is-lit' : ''} ${n <= active && active >= 3 ? 'is-critical' : ''} ${n === active && active > 0 ? 'is-hot' : ''}`}>
            x{n}
          </span>
        ))}
        {active === 5 && <span className="combo-max-label">MAX</span>}
      </div>
      {bestCombo >= 3 && <span className="combo-best">BEST x{bestCombo}</span>}
    </div>
  );
}

function CandleChart({ points, direction, markers }) {
  const canvasRef = useRef(null);
  const sizeRef   = useRef({ W:0, H:0 });
  const displayPointsRef = useRef([]);
  const animRef = useRef(null);

  const draw = useCallback((cv, data) => {
    const { W, H } = sizeRef.current;
    if (!W || !H || data.length < 2) return;
    const ctx = cv.getContext('2d');
    const slice = data.slice(-260);
    if(slice.length < 2) return;

    const bottomPad = 28;
    const rightPad = 16;
    const plotRight = Math.max(40, W - rightPad);
    const plotW = plotRight;
    const toX = i => (slice.length === 1 ? 0 : i * plotW / (slice.length - 1));
    const clamp = (v, loV, hiV) => Math.max(loV, Math.min(hiV, v));
    const lerp = (a, b, t) => a + (b - a) * t;

    const pointAtTs = (ts) => {
      if(ts <= slice[0].ts) return { idx: 0, price: slice[0].price, x: toX(0) };
      const lastIdx = slice.length - 1;
      if(ts >= slice[lastIdx].ts) return { idx: lastIdx, price: slice[lastIdx].price, x: toX(lastIdx) };
      let hiIdx = slice.findIndex(p => p.ts >= ts);
      if(hiIdx <= 0) hiIdx = 1;
      const loIdx = hiIdx - 1;
      const a = slice[loIdx], b = slice[hiIdx];
      const span = b.ts - a.ts || 1;
      const t = clamp((ts - a.ts) / span, 0, 1);
      const virtualIdx = lerp(loIdx, hiIdx, t);
      return { idx: virtualIdx, price: lerp(a.price, b.price, t), x: toX(virtualIdx) };
    };

    const sliceStart = slice[0].ts;
    const sliceEnd = slice[slice.length - 1].ts;
    const visibleMarkers = (markers || [])
      .filter(m => m.ts && Number.isFinite(Number(m.price)) && m.ts >= sliceStart && m.ts <= sliceEnd)
      .map(m => ({ ...m, linePoint: pointAtTs(m.ts) }));
    const prices = slice.map(p=>p.price).concat(visibleMarkers.map(m=>Number(m.price)));
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const pad  = (maxP - minP) * 0.12 || 1;
    const lo   = minP - pad;
    const hi   = maxP + pad;
    const range = hi - lo;
    const toY = p => (H - bottomPad - 8) * (1 - (p - lo) / range) + 4;

    ctx.fillStyle = '#0B1E4A';
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for(let i=1; i<=3; i++){
      const y = (H-bottomPad) * i / 4 + 4;
      ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke();
      const val = hi - (range * i / 4);
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px "Share Tech Mono",monospace';
      ctx.textAlign = 'right';
      ctx.fillText(val.toFixed(4), W-3, y-3);
    }

    const firstP = slice[0]?.price ?? 0;
    const lastP = slice[slice.length-1]?.price ?? 0;
    const isUp = lastP >= firstP;
    const GREEN_LINE = '#2ECC71';
    const lineCol = isUp ? GREEN_LINE : RED;
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    if(!isUp){
      grad.addColorStop(0, 'rgba(232,69,60,0.30)');
      grad.addColorStop(0.62, 'rgba(232,69,60,0.05)');
      grad.addColorStop(1, 'rgba(232,69,60,0)');
    } else {
      grad.addColorStop(0, 'rgba(46,204,113,0.26)');
      grad.addColorStop(0.62, 'rgba(46,204,113,0.05)');
      grad.addColorStop(1, 'rgba(46,204,113,0)');
    }

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, H - bottomPad);
    slice.forEach((p, i) => ctx.lineTo(toX(i), toY(p.price)));
    ctx.lineTo(plotRight, H - bottomPad);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = lineCol;
    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    slice.forEach((p, i) => {
      const x = toX(i), y = toY(p.price);
      if(i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    const last = slice[slice.length - 1];
    const lx = plotRight;
    const ly = toY(last.price);
    ctx.strokeStyle = isUp ? 'rgba(46,204,113,0.36)' : 'rgba(232,69,60,0.36)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(plotRight, ly); ctx.stroke();
    ctx.setLineDash([]);

    const pulseR = 9 + Math.sin(Date.now() / 140) * 2.2;
    ctx.beginPath();
    ctx.arc(lx, ly, pulseR, 0, Math.PI*2);
    ctx.fillStyle = isUp ? 'rgba(46,204,113,0.18)' : 'rgba(232,69,60,0.18)';
    ctx.fill();
    ctx.save();
    ctx.shadowColor = lineCol;
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(lx, ly, 4.8, 0, Math.PI*2);
    ctx.fillStyle = lineCol;
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = '#0B1E4A';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(lx, ly, 4.8, 0, Math.PI*2);
    ctx.stroke();

    if (visibleMarkers.length > 0) {
      const placed = [];
      const padRect = 7;
      const overlaps = (a, b) => (
        a.x < b.x + b.w + padRect &&
        a.x + a.w + padRect > b.x &&
        a.y < b.y + b.h + padRect &&
        a.y + a.h + padRect > b.y
      );
      const overlapArea = (a, b) => {
        const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
        const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
        return ix * iy;
      };
      const placeRect = (x, y, w, h, side) => {
        const laneGap = h + 8;
        const verticalOffsets = side === 'top'
          ? [0, -laneGap, laneGap, -laneGap * 2, laneGap * 2, -laneGap * 3, laneGap * 3]
          : [0, laneGap, -laneGap, laneGap * 2, -laneGap * 2, laneGap * 3, -laneGap * 3];
        const horizontalOffsets = [0, 18, -18, 36, -36, 54, -54, 72, -72];
        let best = null;

        for (const yOffset of verticalOffsets) {
          for (const xOffset of horizontalOffsets) {
            const rect = {
              x: clamp(x + xOffset, 4, W - w - 4),
              y: clamp(y + yOffset, 4, H - h - bottomPad - 4),
              w, h,
            };
            const area = placed.reduce((sum, p) => sum + overlapArea(rect, p), 0);
            const collisions = placed.filter(p => overlaps(rect, p)).length;
            const score = collisions * 100000 + area + Math.abs(yOffset) * 6 + Math.abs(xOffset);
            if (!best || score < best.score) best = { ...rect, score };
            if (collisions === 0) {
              placed.push(rect);
              return rect;
            }
          }
        }

        const rect = best || { x: clamp(x, 4, W - w - 4), y: clamp(y, 4, H - h - bottomPad - 4), w, h };
        placed.push(rect);
        return rect;
      };

      const sortedMarkers = [...visibleMarkers].sort((a,b)=>a.ts-b.ts);
      for (const m of sortedMarkers) {
        const isBuy = m.dir === 1;
        const col = isBuy ? '#2ECC71' : RED;
        const mx = m.linePoint.x;
        const my = toY(m.linePoint.price);
        const label = `${isBuy ? 'BUY' : 'SELL'}@${Number(m.price).toFixed(4)}`;
        const bw = Math.max(86, Math.min(116, label.length * 7 + 18));
        const bh = 24;
        const side = isBuy ? 'top' : 'bottom';
        const baseY = side === 'top' ? my - bh - 32 : my + 26;
        const rect = placeRect(mx - bw / 2, baseY, bw, bh, side);
        const anchorX = clamp(mx, rect.x + 10, rect.x + rect.w - 10);
        const edgeY = side === 'top' ? rect.y + rect.h : rect.y;

        ctx.strokeStyle = col;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(mx, my);
        ctx.lineTo(anchorX, edgeY);
        ctx.stroke();

        ctx.fillStyle = col;
        ctx.beginPath();
        if(ctx.roundRect) ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 6);
        else ctx.rect(rect.x, rect.y, rect.w, rect.h);
        ctx.fill();

        ctx.beginPath();
        if(side === 'top'){
          ctx.moveTo(anchorX - 5, rect.y + rect.h - 1);
          ctx.lineTo(anchorX + 5, rect.y + rect.h - 1);
          ctx.lineTo(anchorX, rect.y + rect.h + 6);
        } else {
          ctx.moveTo(anchorX - 5, rect.y + 1);
          ctx.lineTo(anchorX + 5, rect.y + 1);
          ctx.lineTo(anchorX, rect.y - 6);
        }
        ctx.closePath();
        ctx.fill();

        ctx.save();
        ctx.shadowColor = col;
        ctx.shadowBlur = 8;
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(mx, my, 4.7, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        ctx.strokeStyle = '#0B1E4A';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(mx, my, 4.7, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = isBuy ? '#000' : '#fff';
        ctx.font = 'bold 10px "Share Tech Mono",monospace';
        ctx.textAlign = 'center';
        ctx.fillText(label, rect.x + rect.w / 2, rect.y + 16);
      }
    }

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 13px "Share Tech Mono",monospace';
    ctx.textAlign = 'left';
    const t0 = new Date(slice[0].ts).toLocaleTimeString('zh-CN',{hour12:false,hour:'2-digit',minute:'2-digit'});
    const tN = new Date(last.ts).toLocaleTimeString('zh-CN',{hour12:false,hour:'2-digit',minute:'2-digit'});
    ctx.fillText(t0, 4, H - 8);
    ctx.textAlign = 'right';
    ctx.fillText(tN, W - 4, H - 8);
  }, [direction, markers]);

  const animateToPoints = useCallback((target) => {
    const cv = canvasRef.current;
    if(!cv || target.length === 0) return;
    if(animRef.current) cancelAnimationFrame(animRef.current);

    const startPoints = displayPointsRef.current.length > 0 ? displayPointsRef.current : target;
    if(startPoints.length === 0){
      displayPointsRef.current = target;
      draw(cv, target);
      return;
    }

    const byTs = new Map(startPoints.map(p => [p.ts, p]));
    const lastStart = startPoints[startPoints.length - 1];
    const lerp = (a, b, t) => a + (b - a) * t;
    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
    const started = performance.now();
    const duration = 280;

    const frame = (now) => {
      const t = Math.min(1, (now - started) / duration);
      const k = easeOutCubic(t);
      const next = target.map((p, i) => {
        const prevByTime = byTs.get(p.ts);
        const fallbackPrice = byTs.get(target[i - 1]?.ts)?.price ?? lastStart?.price ?? p.price;
        const from = prevByTime || { ...p, price: fallbackPrice };
        return {
          ...p,
          price: lerp(from.price, p.price, k),
        };
      });
      displayPointsRef.current = next;
      draw(cv, next);
      if(t < 1) animRef.current = requestAnimationFrame(frame);
      else {
        displayPointsRef.current = target;
        draw(cv, target);
      }
    };

    animRef.current = requestAnimationFrame(frame);
  }, [draw]);

  useEffect(()=>{
    const cv = canvasRef.current; if(!cv) return;
    const set = () => {
      const r = cv.getBoundingClientRect(), dpr = window.devicePixelRatio||1;
      const W = Math.floor(r.width*dpr), H = Math.floor(r.height*dpr);
      if(!W||!H) return;
      cv.width = W; cv.height = H;
      cv.getContext('2d').scale(dpr, dpr);
      sizeRef.current = { W: r.width, H: r.height };
      draw(cv, displayPointsRef.current.length > 0 ? displayPointsRef.current : (cv._lastPoints || []));
    };
    set();
    const ro = new ResizeObserver(set); ro.observe(cv);
    return () => {
      ro.disconnect();
      if(animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [draw]);

  useEffect(()=>{
    if(canvasRef.current && points.length > 0){
      const target = points.slice(-320).map(p => ({...p}));
      canvasRef.current._lastPoints = target;
      animateToPoints(target);
    }
  }, [points, animateToPoints]);

  return <canvas ref={canvasRef} style={{width:'100%',height:'100%',display:'block'}} />;
}

/* ── 主游戏页面 ── */
export default function Game() {
  const { level }       = useParams();
  const { user, token, refreshUser } = useApp();
  const { t, lang }     = useLang();
  const nav             = useNavigate();
  const ws              = useRef(null);
  const roomCfg         = ROOM_CONFIG[level] || ROOM_CONFIG[1];

  const [gameState, setGameState]       = useState('WAITING');
  const [busyRoom, setBusyRoom]         = useState(null);
  const [playerCount, setPC]            = useState(1);
  const [isReady, setIsReady]           = useState(false);
  const [readyPending, setReadyPending] = useState(false);
  const [countdown, setCountdown]       = useState(5);
  const [remain, setRemain]             = useState(60);
  const [direction, setDirection]       = useState(null);
  const [openPrice, setOpenPrice]       = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [currentPts, setCurrentPts]     = useState(null);
  const [isBust, setIsBust]             = useState(false);
  const [pricePoints, setPricePoints]   = useState([]);
  const [settled, setSettled]           = useState(null);
  const [errMsg, setErr]                = useState('');
  const [kicked, setKicked]             = useState('');
  const [notice, setNotice]             = useState({ text: '', url: '' });
  const [switching, setSwitching]       = useState(false);
  const [tradeLogs, setTradeLogs]       = useState([]);
  const [bullets, setBullets]           = useState([]);
  const [bustPool, setBustPool]         = useState(0);
  const [flashMsg, setFlashMsg]         = useState(null);
  const [topNotice, setTopNotice]       = useState(null);
  const [pendingDir, setPendingDir]     = useState(null);
  const [dangerMode, setDangerMode]     = useState(false); // 积分超2倍时激活
  const [comboInfo, setComboInfo]       = useState({ streak: 0, best: 0, totalBonus: 0 });
  const [comboPop, setComboPop]         = useState(null);
  const [segSettle, setSegSettle]       = useState(null);   // 切换结算卡片 {pnl, bonus, streak, won}
  const [firstTip,  setFirstTip]        = useState(false);  // 首次进入提示
  const segSettleTimer = useRef(null);

  const carryPts      = useRef(roomCfg.carry);
  const timerRef      = useRef(null);
  const cdRef         = useRef(null);
  const startedAtRef  = useRef(null);
  const directionRef  = useRef(null);
  const openPriceRef  = useRef(null);
  const openPtsRef    = useRef(null);   // 建仓时扣完spread后的积分基准
  const currentPtsRef = useRef(null);
  const currentRoomIdRef = useRef(null);
  const logId         = useRef(0);
  const bulletId      = useRef(0);
  const historyLoaded = useRef(false);
  const handleMsgRef  = useRef(null);
  const hbLoopRef     = useRef(null);
  const countdownLoopRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const errTimerRef = useRef(null);
  const comboPopTimerRef = useRef(null);
  const pendingDirectionRef = useRef(null);
  const switchTimeoutRef = useRef(null);
  const pricePointTsRef = useRef(0);
  const lastPriceSfxRef = useRef({ price: null, ts: 0 });
  const bustSoundPlayedRef = useRef(false);

  // 音频预加载与页面级清理
  useEffect(() => {
    initAudio();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (cdRef.current) clearInterval(cdRef.current);
      if (hbLoopRef.current) hbLoopRef.current.stop();
      if (countdownLoopRef.current) countdownLoopRef.current.stop();
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
      if (errTimerRef.current) clearTimeout(errTimerRef.current);
      if (comboPopTimerRef.current) clearTimeout(comboPopTimerRef.current);
      cleanupAudio();
    };
  }, []);

  // 监听 currentPts，超过 2 倍 carryPts 触发危险模式
  useEffect(() => {
    if (currentPts == null || !carryPts.current) return;
    const isDanger = currentPts >= carryPts.current * 2;
    setDangerMode(isDanger);
    if (isDanger) {
      if (!hbLoopRef.current) hbLoopRef.current = playLoopSfx('heart-stop-heartbeat', 0.7);
    } else {
      if (hbLoopRef.current) {
        hbLoopRef.current.stop();
        hbLoopRef.current = null;
      }
    }
  }, [currentPts]);

  const pushBullet = useCallback((text, type='info') => {
    const id = bulletId.current++;
    setBullets(prev => [...prev.slice(-2), { id, text, type }]);
    setTimeout(()=>setBullets(prev=>prev.filter(b=>b.id!==id)), 3000);
  }, []);

  const showTopNotice = useCallback((text, type='info', duration=1800) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setTopNotice({ id: Date.now(), text, type });
    noticeTimerRef.current = setTimeout(() => setTopNotice(null), duration);
  }, []);

  const clearError = useCallback(() => {
    if (errTimerRef.current) clearTimeout(errTimerRef.current);
    errTimerRef.current = null;
    setErr('');
  }, []);

  const showError = useCallback((text, duration = 2200) => {
    if (errTimerRef.current) clearTimeout(errTimerRef.current);
    setErr(text || '未知错误');
    errTimerRef.current = setTimeout(() => {
      setErr('');
      errTimerRef.current = null;
    }, duration);
  }, []);

  const refreshUserQuietly = useCallback(() => {
    if (typeof refreshUser === 'function') refreshUser().catch(() => {});
  }, [refreshUser]);

  const goLobby = useCallback(() => {
    refreshUserQuietly();
    nav('/lobby');
  }, [nav, refreshUserQuietly]);

  const goRetry = useCallback(() => {
    refreshUserQuietly();
    nav(`/game/${level}`);
  }, [level, nav, refreshUserQuietly]);

  const stopCountdownSfx = useCallback(() => {
    if (countdownLoopRef.current) {
      countdownLoopRef.current.stop();
      countdownLoopRef.current = null;
    }
  }, []);

  const startCountdownSfx = useCallback(() => {
    stopCountdownSfx();
    countdownLoopRef.current = playLoopSfx('ticking-clock', 0.72);
  }, [stopCountdownSfx]);

  const playPriceMoveSfx = useCallback((nextPrice) => {
    const prevPrice = lastPriceSfxRef.current.price;
    lastPriceSfxRef.current.price = nextPrice;
    if (prevPrice == null) return;

    const delta = nextPrice - prevPrice;
    const minMove = Math.max(0.000001, Math.abs(prevPrice) * 0.000002);
    if (Math.abs(delta) < minMove) return;

    const now = Date.now();
    if (now - lastPriceSfxRef.current.ts < 500) return;
    lastPriceSfxRef.current.ts = now;

    if (delta > 0) playSfx('score-up', 0.42, 1.35);
    else playSfx('short-circuit', 0.28, 1.8);
  }, []);

  useEffect(() => {
    if (['WAITING', 'READYING', 'COUNTDOWN', 'RUNNING'].includes(gameState) && !kicked) {
      playBgm('game-bgm');
    } else {
      stopBgm();
    }
  }, [gameState, kicked]);

  /* 跑马灯公告 */
  useEffect(() => {
    API.get('/api/game/notice').then(d => { if (d.text) setNotice(d); }).catch(() => {});
  }, []);

  /* 历史K线 */
  useEffect(()=>{
    if(historyLoaded.current) return;
    historyLoaded.current = true;
    API.get('/api/game/kline-history?n=100')
      .then(d=>{
        if(d.bars?.length>0) {
          const loadedPoints = d.bars.map(b => ({ ts: b.ts, price: b.close }));
          pricePointTsRef.current = loadedPoints[loadedPoints.length - 1]?.ts || 0;
          lastPriceSfxRef.current.price = loadedPoints[loadedPoints.length - 1]?.price ?? null;
          setPricePoints(loadedPoints);
        }
      })
      .catch(()=>{});
  },[]);

  /* WS */
  useEffect(()=>{
    if(!token){ nav('/'); return; }
    const sock = createWS();
    ws.current = sock;
    sock.onopen    = ()=> { clearError(); sock.send(JSON.stringify({type:'AUTH',payload:{token}})); };
    sock.onmessage = (e)=>{ try{ handleMsgRef.current?.(JSON.parse(e.data)); }catch(ex){} };
    sock.onerror   = ()=> showError('连接失败，请刷新重试', 4000);
    sock.onclose   = ()=> { showError('连接已断开，请刷新页面重新进入', 5000); setReadyPending(false); setSwitching(false); setPendingDir(null); };
    return ()=> sock.close();
  },[token, clearError, showError]);// eslint-disable-line

  const addPricePoint = useCallback((p, ts = Date.now()) => {
    const price = Number(p);
    if (!Number.isFinite(price) || price <= 0) return null;
    const pointTs = ts <= pricePointTsRef.current ? pricePointTsRef.current + 1 : ts;
    pricePointTsRef.current = pointTs;
    setPricePoints(prev => appendPricePoint(prev, price, pointTs));
    return pointTs;
  }, []);

  const applySelfPlayerSnapshot = useCallback((snapshot, carryValue) => {
    const carry = Number(carryValue) || carryPts.current || roomCfg.carry;
    const initialPts = Math.floor(carry * (1 - SPREAD));
    const finiteOr = (value, fallback) => {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    };
    const nextDirection = snapshot?.direction === 1 || snapshot?.direction === -1
      ? snapshot.direction
      : null;
    const nextOpenPrice = snapshot?.openPrice != null
      ? finiteOr(snapshot.openPrice, null)
      : null;
    const nextOpenPts = snapshot?.openPts != null
      ? finiteOr(snapshot.openPts, initialPts)
      : initialPts;
    const nextCurrentPts = snapshot?.currentPts != null
      ? finiteOr(snapshot.currentPts, initialPts)
      : initialPts;
    const nextBust = snapshot?.isBust === true;

    carryPts.current = carry;
    directionRef.current = nextDirection;
    openPriceRef.current = nextOpenPrice;
    openPtsRef.current = nextOpenPts;
    currentPtsRef.current = nextBust ? 0 : nextCurrentPts;
    setDirection(nextDirection);
    setOpenPrice(nextOpenPrice);
    setCurrentPts(nextBust ? 0 : nextCurrentPts);
    setIsBust(nextBust);
    setComboInfo({
      streak: finiteOr(snapshot?.comboStreak, 0),
      best: finiteOr(snapshot?.bestCombo, 0),
      totalBonus: finiteOr(snapshot?.comboBonusPts, 0),
    });
    bustSoundPlayedRef.current = nextBust;
    setSwitching(false);
    setPendingDir(null);
    pendingDirectionRef.current = null;
  }, [roomCfg.carry]);

  const handleMsg = useCallback((msg)=>{
    switch(msg.type){
      case 'AUTH_OK':
        clearError();
        currentRoomIdRef.current=null;
        ws.current.send(JSON.stringify({type:'JOIN_ROOM',payload:{level:parseInt(level)}}));
        break;
      case 'JOINED_ROOM':
        clearError();
        setBusyRoom(null);
        currentRoomIdRef.current=msg.roomId;
        setReadyPending(false);
        setIsReady(msg.selfReady === true);
        if (msg.playerCount != null) setPC(msg.playerCount);
        applySelfPlayerSnapshot(msg.selfPlayer, msg.carryPts);
        if(msg.status==='SETTLING') setGameState('SETTLING');
        lastPriceSfxRef.current.ts=0;
        setComboPop(null);
        break;
      case 'ROOM_STATE':
        setPC(msg.playerCount);
        if(typeof msg.selfReady==='boolean') setIsReady(msg.selfReady);
        if(msg.selfPlayer) applySelfPlayerSnapshot(msg.selfPlayer, msg.carryPts);
        if(msg.status==='WAITING') setGameState('WAITING');
        if(msg.status==='READYING') setGameState('READYING');
        if(msg.status==='COUNTDOWN') {
          setGameState('COUNTDOWN');
          if(msg.countdownSeconds!=null) setCountdown(msg.countdownSeconds);
        }
        if(msg.status==='SETTLING') setGameState('SETTLING');
        if(msg.status==='RUNNING'){
          setGameState('RUNNING');
          const serverStartedAt=Number(msg.startedAt)||Date.now();
          if(!startedAtRef.current||Math.abs(startedAtRef.current-serverStartedAt)>1000) startGameTimer(serverStartedAt);
        }
        break;
      case 'READY_ACK':
        setReadyPending(false);
        setIsReady(true);
        if(msg.playerCount!=null) setPC(msg.playerCount);
        break;
      case 'PLAYER_JOINED': setPC(msg.playerCount); break;
      case 'PLAYER_LEFT':
        if(msg.playerCount!=null) setPC(msg.playerCount);
        break;
      case 'ROOM_BUSY':
        stopCountdownSfx();
        stopBgm();
        clearError();
        setBusyRoom(msg.currentRoom||null);
        if(msg.currentRoom?.playerCount!=null) setPC(msg.currentRoom.playerCount);
        setReadyPending(false);
        setIsReady(false);
        setGameState('WAIT_NEXT');
        break;
      case 'COUNTDOWN':
        initAudio();
        startCountdownSfx();
        setGameState('COUNTDOWN'); setCountdown(msg.seconds);
        clearInterval(cdRef.current);
        let n=msg.seconds; cdRef.current=setInterval(()=>{n--;setCountdown(n);if(n<=0){clearInterval(cdRef.current);stopCountdownSfx();}},1000);
        break;
      case 'COUNTDOWN_CANCELLED':
        stopCountdownSfx();
        clearInterval(cdRef.current);
        setCountdown(5);
        if(msg.playerCount!=null) setPC(msg.playerCount);
        setGameState(msg.status==='READYING'?'READYING':'WAITING');
        showTopNotice(msg.reason||'人数发生变化，已取消倒计时','info',2200);
        break;
      case 'GAME_START':
        clearError();
        stopCountdownSfx();
        setReadyPending(false);
        playBgm('game-bgm');
        setGameState('RUNNING');
        setComboPop(null);
        { const serverStartedAt=Number(msg.startedAt)||startedAtRef.current||Date.now();
          if(!startedAtRef.current||Math.abs(startedAtRef.current-serverStartedAt)>1000) startGameTimer(serverStartedAt); }
        // 首次游戏提示（每个设备只显示一次）
        if(!localStorage.getItem('combo_tip_shown')){
          setFirstTip(true);
          localStorage.setItem('combo_tip_shown','1');
          setTimeout(()=>setFirstTip(false), 4000);
        }
        break;
      case 'GAME_CANCELLED':
        if(!isCurrentRoomMessage(msg.roomId,currentRoomIdRef.current)) break;
        stopCountdownSfx();
        stopBgm();
        setReadyPending(false);
        refreshUserQuietly();
        setKicked(msg.reason||'对局取消');
        setTimeout(goLobby,3000);
        break;
      case 'PRICE_UPDATE':{
        const p=Number(msg.price);
        if(!isNaN(p)&&p>0){setCurrentPrice(p);playPriceMoveSfx(p);addPricePoint(p);}
        const u=msg.updates?.find(u=>u.userId===user?.id);
        if(u?.currentPts!=null){currentPtsRef.current=u.currentPts;setCurrentPts(u.currentPts);}
        break;
      }
      case 'POSITION_ACK':{
        if(switchTimeoutRef.current){ clearTimeout(switchTimeoutRef.current); switchTimeoutRef.current=null; }
        const nOpen=Number(msg.openPrice),nPts=Number(msg.openPts);
        const pending = pendingDirectionRef.current;
        const prevDir = pending?.prevDir ?? directionRef.current;
        const nextDir = pending?.nextDir ?? directionRef.current;
        if(!Number.isFinite(nOpen)||!Number.isFinite(nPts)||![1,-1].includes(nextDir)){
          setSwitching(false); setPendingDir(null); pendingDirectionRef.current=null;
          showTopNotice(lang==='en'?'Open position error, please retry':'开仓确认异常，请重试','loss');
          break;
        }
        const now = Date.now();
        const nextLabel = nextDir === 1 ? t('longUp') : t('shortDown');
        const markerTs = addPricePoint(nOpen, now) || now;
        const nextMarker = {id:logId.current++,kind:'entry',dir:nextDir,ts:markerTs,price:nOpen};
        const hadPreviousSegment = Boolean(msg.hadPreviousSegment ?? (openPriceRef.current!=null&&prevDir!=null));
        const rawSegmentPnl = Number(msg.segmentPnl);
        const segmentPnl = Number.isFinite(rawSegmentPnl) ? rawSegmentPnl : 0;
        const rawCombo = Number(msg.comboMultiplier ?? msg.comboStreak);
        const comboMultiplier = Number.isFinite(rawCombo) ? Math.max(0, rawCombo) : 0;
        const rawBonus = Number(msg.comboBonusPts);
        const comboBonusPts = Number.isFinite(rawBonus) ? Math.max(0, rawBonus) : 0;
        const rawTotalBonus = Number(msg.comboTotalBonusPts);
        const comboTotalBonusPts = Number.isFinite(rawTotalBonus) ? Math.max(0, rawTotalBonus) : null;
        const rawBestCombo = Number(msg.bestCombo);
        const bestCombo = Number.isFinite(rawBestCombo) ? Math.max(0, rawBestCombo) : comboMultiplier;

        if(hadPreviousSegment){
          const pnlText = `${segmentPnl>=0?'+':''}${Math.round(segmentPnl).toLocaleString()}`;
          setTradeLogs(prev=>[...prev,nextMarker].slice(-40));
          // 切换结算卡片
          if(segSettleTimer.current) clearTimeout(segSettleTimer.current);
          setSegSettle({ pnl: Math.round(segmentPnl), bonus: Math.round(comboBonusPts), streak: comboMultiplier, won: segmentPnl > 0 });
          segSettleTimer.current = setTimeout(() => setSegSettle(null), 2800);
          if(segmentPnl>0){
            SFX.combo(comboMultiplier || 1);
            if(comboBonusPts>0) setTimeout(()=>SFX.comboCash(), Math.min(180 + comboMultiplier * 30, 420));
            setComboInfo(prev=>({
              streak: comboMultiplier,
              best: Math.max(prev.best || 0, bestCombo, comboMultiplier),
              totalBonus: comboTotalBonusPts ?? ((prev.totalBonus || 0) + comboBonusPts),
            }));
            if(comboPopTimerRef.current) clearTimeout(comboPopTimerRef.current);
            setComboPop({ id: Date.now(), multiplier: comboMultiplier || 1, bonus: comboBonusPts });
            comboPopTimerRef.current = setTimeout(()=>{
              setComboPop(null);
              comboPopTimerRef.current = null;
            }, comboMultiplier >= 3 ? 1050 : 780);
            setFlashMsg({text:pnlText,color:GOLD});
          }
          else if(segmentPnl<0){
            SFX.loss();
            setComboInfo(prev=>({...prev,streak:0,totalBonus:comboTotalBonusPts ?? prev.totalBonus}));
            if(comboPopTimerRef.current) clearTimeout(comboPopTimerRef.current);
            comboPopTimerRef.current = null;
            setComboPop(null);
            setFlashMsg({text:pnlText,color:RED});
          }
          else {
            setComboInfo(prev=>({...prev,streak:0,totalBonus:comboTotalBonusPts ?? prev.totalBonus}));
            if(comboPopTimerRef.current) clearTimeout(comboPopTimerRef.current);
            comboPopTimerRef.current = null;
            setComboPop(null);
            setFlashMsg({text:'0',color:'#888'});
          }
          const comboText = segmentPnl > 0
            ? `x${comboMultiplier || 1}${comboBonusPts>0 ? ` ${lang==='en'?'Crit':' 暴击'} +${Math.round(comboBonusPts).toLocaleString()}` : ''}`
            : (lang==='en'?'Streak Reset':'连胜重置');
          const segLabel = lang==='en' ? `Seg ${pnlText}` : `本段 ${pnlText}`;
          showTopNotice(`${segLabel} · ${comboText} · ${nextLabel}`, segmentPnl>0?'profit':segmentPnl<0?'loss':'info', segmentPnl>0&&comboMultiplier>=3?2200:1800);
          setTimeout(()=>setFlashMsg(null),1400);
        } else {
          SFX.open();
          setTradeLogs(prev=>[...prev,nextMarker].slice(-40));
          showTopNotice(lang==='en'?`${nextLabel} opened`:`${nextLabel}已开仓`, nextDir===1?'buy':'sell');
        }
        clearError();
        openPriceRef.current=nOpen; openPtsRef.current=nPts; directionRef.current=nextDir;
        currentPtsRef.current=nPts;
        pendingDirectionRef.current=null;
        setOpenPrice(nOpen);setCurrentPts(nPts);setDirection(nextDir);setPendingDir(null);setSwitching(false); break;
      }
      case 'PLAYER_BUST':
        if(msg.userId===user?.id){
          setIsBust(true);setGameState('BUST');
          if(!bustSoundPlayedRef.current){
            bustSoundPlayedRef.current=true;
            SFX.lose();
            setTimeout(()=>SFX.bust(),80);
          }
        }
        if(msg.playerCount!=null) setPC(msg.playerCount);
        setBustPool(p=>p+carryPts.current);
        if(!msg.isBot) pushBullet(lang==='en'?`Player …${String(msg.userId).slice(-4)} busted  Pool +${carryPts.current.toLocaleString()}`:`玩家${String(msg.userId).slice(-4)} 爆仓  奖池 +${carryPts.current.toLocaleString()}`,'bust');
        break;
      case 'YOU_BUST':
        if(!isCurrentRoomMessage(msg.roomId,currentRoomIdRef.current)) break;
        setIsBust(true); setGameState('BUST');
        if(hbLoopRef.current){hbLoopRef.current.stop();hbLoopRef.current=null;}
        if(!bustSoundPlayedRef.current){
          bustSoundPlayedRef.current=true;
          SFX.lose();
          setTimeout(()=>SFX.bust(),80);
        }
        setDangerMode(false);
        break;
      case 'GAME_SETTLE':{
        stopCountdownSfx();
        stopBgm();
        setGameState('ENDED');clearInterval(timerRef.current);
        if(hbLoopRef.current){hbLoopRef.current.stop();hbLoopRef.current=null;}
        setDangerMode(false);
        const r=msg.results?.find(r=>r.userId===user?.id);
        setSettled({...(r||{}),bustPool:msg.bustPool||0,allResults:msg.results||[],carryPtsVal:msg.carryPts||carryPts.current});
        refreshUserQuietly();
        const settlePts = r?.settlePts||0;
        const netPts = settlePts - carryPts.current;
        if(settlePts>=carryPts.current){
          if(netPts>=carryPts.current*0.5)SFX.jackpot();else SFX.settleWin();
        }else if(bustSoundPlayedRef.current) SFX.lose();
        else SFX.settleLose(); break;
      }
      case 'KICKED':
        if(!isCurrentRoomMessage(msg.roomId,currentRoomIdRef.current)) break;
        stopCountdownSfx();
        stopBgm();
        setReadyPending(false);
        setIsReady(false);
        refreshUserQuietly();
        setKicked(msg.reason||'已被移出');
        setTimeout(goLobby,3000);
        break;
      case 'ERROR':
        if(switchTimeoutRef.current){ clearTimeout(switchTimeoutRef.current); switchTimeoutRef.current=null; }
        setReadyPending(false);
        showError(msg.msg||'未知错误'); setSwitching(false); setPendingDir(null); pendingDirectionRef.current=null;
        showTopNotice('操作失败，请重试','loss');
        break;
      default: break;
    }
  },[user,level,addPricePoint,applySelfPlayerSnapshot,pushBullet,playerCount,showTopNotice,clearError,showError,startCountdownSfx,stopCountdownSfx,playPriceMoveSfx,refreshUserQuietly,goLobby]);// eslint-disable-line

  useEffect(()=>{ handleMsgRef.current=handleMsg; },[handleMsg]);

  function startGameTimer(serverStartedAt=Date.now()){
    const parsed=Number(serverStartedAt);
    const start=Number.isFinite(parsed)&&parsed>0?parsed:Date.now();
    startedAtRef.current=start;
    if(timerRef.current) clearInterval(timerRef.current);
    const update=()=>{
      const r=Math.max(0,Math.ceil((60000-(Date.now()-start))/1000));
      setRemain(r);
      if(r===5) pushBullet(lang==='en'?'Last 5 seconds!':'最后 5 秒！','warn');
      if(r<=0&&timerRef.current){clearInterval(timerRef.current);timerRef.current=null;}
    };
    update();
    if(Date.now()-start<60000) timerRef.current=setInterval(update,1000);
  }
  function sendReady(){
    if(!ws.current||ws.current.readyState!==1||isReady||readyPending) return;
    initAudio();
    playBgm('game-bgm');
    SFX.click();
    setReadyPending(true);
    try {
      ws.current.send(JSON.stringify({type:'READY',payload:{}}));
    } catch {
      setReadyPending(false);
      showError('准备失败，请重试');
    }
  }
  function switchDir(dir){
    if(!ws.current||isBust||switching) return;
    if(dir===directionRef.current) return;
    // 游戏未在进行中时禁止建仓
    if(gameState!=='RUNNING') return;
    initAudio();
    playBgm('game-bgm');
    const prevDir = directionRef.current;
    pendingDirectionRef.current = { prevDir, nextDir: dir };
    SFX.click(); setSwitching(true); setPendingDir(dir);
    showTopNotice(lang==='en'?`${dir===1?t('longUp'):t('shortDown')} submitting...`:`${dir===1?t('longUp'):t('shortDown')}提交中...`, dir===1?'buy':'sell', 1200);
    ws.current.send(JSON.stringify({type:'OPEN_POSITION',payload:{direction:dir}}));
    // switching 超时保护：3秒无响应自动重置
    if(switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
    switchTimeoutRef.current = setTimeout(()=>{ setSwitching(false); setPendingDir(null); pendingDirectionRef.current=null; }, 3000);
  }

  const pnlRate = (direction!=null&&openPrice&&currentPrice&&openPrice>0)
    ? direction*(currentPrice-openPrice)/openPrice*LEVERAGE : 0;
  const pnlPct  = (pnlRate*100).toFixed(2);
  const isProfit= pnlRate>=0;
  const activeDir = pendingDir ?? direction;
  const floatingPts = isBust ? 0
    : (direction!=null&&currentPts!=null&&openPtsRef.current!=null)
      ? currentPts - openPtsRef.current : 0;
  const floatingPtsText = `${floatingPts>=0?'+':''}${Math.round(floatingPts).toLocaleString()}`;
  const floatingIsProfit = floatingPts >= 0;
  const heroPrice = currentPrice ?? (pricePoints.length > 0 ? pricePoints[pricePoints.length - 1].price : null);
  const currentPtsClass = currentPts == null
    ? ''
    : currentPts <= carryPts.current * 0.2
      ? 'is-danger'
      : currentPts >= carryPts.current
        ? 'is-profit'
        : 'is-loss';
  const currentPtsRatio = currentPts != null && carryPts.current
    ? Math.max(0, Math.min(100, currentPts / carryPts.current * 100))
    : 0;

  /* ── CSS ── */
  const S = `
    .g-root{display:flex;flex-direction:column;height:100vh;height:100dvh;background:#0B1E4A;overflow:hidden;}
    .g-header{display:flex;align-items:center;justify-content:space-between;
      padding:52px 20px 16px;flex-shrink:0;}
    .g-title{font-size:17px;font-weight:700;color:#fff;text-align:center;flex:1;}
    .g-back{width:36px;height:36px;border-radius:50%;background:#0F2554;border:none;
      color:#ffffff;font-size:20px;font-weight:900;cursor:pointer;display:flex;align-items:center;justify-content:center;}
    .g-chart-area{flex:1;min-height:0;padding:0 0 4px;position:relative;}
    .g-price-hero{padding:0 20px 12px;flex-shrink:0;}
    .g-price-big{font-size:36px;font-weight:900;color:#fff;letter-spacing:0;line-height:1.05;
      display:flex;align-items:flex-end;gap:1px;perspective:360px;font-variant-numeric:tabular-nums;}
    .price-flip-cell{position:relative;display:inline-flex;align-items:center;justify-content:center;
      min-width:0.58em;height:1.05em;transform-style:preserve-3d;}
    .price-flip-cell.is-symbol{min-width:0.46em;}
    .price-flip-cell.is-dot{min-width:0.22em;}
    .price-flip-static{display:block;}
    .price-flip-cell.is-flipping .price-flip-static{opacity:0;}
    .price-flip-face{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
      backface-visibility:hidden;transform-origin:center center;will-change:transform,opacity;}
    .price-flip-old{animation:priceFlipOld 0.28s cubic-bezier(0.2,0.7,0.2,1) both;}
    .price-flip-new{animation:priceFlipNew 0.28s cubic-bezier(0.2,0.7,0.2,1) both;}
    .g-price-sub{display:flex;align-items:center;gap:8px;row-gap:6px;margin-top:6px;flex-wrap:wrap;}
    .g-float-chip{padding:3px 10px;border-radius:50px;font-size:12px;font-weight:800;
      font-family:'Share Tech Mono',monospace;animation:gFloatPulse 0.2s ease;}
    .g-live-score{margin-top:12px;border-radius:14px;padding:12px 14px;position:relative;overflow:hidden;
      background:#0F2554;
      border:none;
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.1),0 8px 24px rgba(0,0,0,0.4);}
    .g-live-score:before{content:'';position:absolute;inset:-40% 52% -40% -30%;pointer-events:none;
      background:linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent);
      transform:rotate(18deg);animation:scoreShine 2.2s ease-in-out infinite;}
    .g-live-score.is-profit{border:none;box-shadow:0 0 34px ${GOLD}1f,inset 0 1px 0 rgba(255,255,255,0.12);}
    .g-live-score.is-loss{border:none;box-shadow:0 0 28px ${RED}18,inset 0 1px 0 rgba(255,255,255,0.1);}
    .g-live-score.is-danger{border:none;animation:scoreDanger 0.72s ease-in-out infinite alternate;
      box-shadow:0 0 40px ${RED}38,inset 0 1px 0 rgba(255,255,255,0.12);}
    .g-live-score-top{display:flex;align-items:center;justify-content:space-between;gap:12px;position:relative;z-index:1;}
    .g-live-score-label{font-size:13px;color:#8BA8D4;font-weight:700;letter-spacing:0;text-transform:uppercase;}
    .g-live-score-delta{font-family:'Share Tech Mono',monospace;font-size:14px;font-weight:900;padding:3px 9px;
      border-radius:8px;background:rgba(46,127,232,0.12);white-space:nowrap;}
    .g-live-score-value{margin-top:3px;position:relative;z-index:1;font-family:'Share Tech Mono',monospace;
      font-size:34px;line-height:0.98;font-weight:900;color:#fff;font-variant-numeric:tabular-nums;
      text-shadow:0 0 22px rgba(255,255,255,0.18);animation:scoreTick 0.26s cubic-bezier(0.2,1.4,0.35,1);}
    .g-live-score.is-profit .g-live-score-value,.g-live-score.is-profit .g-live-score-delta{color:${GOLD};
      text-shadow:0 0 22px ${GOLD}55;}
    .g-live-score.is-loss .g-live-score-value,.g-live-score.is-loss .g-live-score-delta{color:${RED};
      text-shadow:0 0 22px ${RED}44;}
    .g-live-score-meter{margin-top:10px;height:10px;border-radius:999px;background:#091840;
      overflow:hidden;position:relative;z-index:1;}
    .g-live-score-fill{height:100%;border-radius:999px;transition:width 0.22s ease,background 0.22s ease;
      background:linear-gradient(180deg,#FFD700 0%,#E6A800 100%) !important;
      box-shadow:0 0 12px rgba(245,197,24,0.6),inset 0 1px 0 rgba(255,255,255,0.3);}
    .combo-meter{margin-top:10px;min-height:38px;display:flex;align-items:center;gap:8px;overflow:hidden;}
    .combo-track{display:flex;align-items:center;gap:5px;max-width:100%;padding:5px 8px;border-radius:10px;
      background:linear-gradient(90deg,#0D2B6B,#152D6A,#0D2B6B);
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.1),0 4px 12px rgba(0,0,0,0.3);}
    .combo-meter.is-live .combo-track{box-shadow:0 0 20px rgba(255,202,58,0.25),inset 0 1px 0 rgba(255,255,255,0.15);}
    .combo-label{font-size:11px;color:#8BA8D4;font-weight:700;margin-right:2px;white-space:nowrap;}
    .combo-cell{width:38px;height:28px;display:flex;align-items:center;justify-content:center;
      font-family:'Share Tech Mono',monospace;font-size:14px;font-style:italic;font-weight:900;
      border-radius:6px;transition:all 0.2s;
      background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.06);}
    .combo-cell.is-lit{background:rgba(255,215,0,0.12);color:rgba(255,215,0,0.6);border-color:rgba(255,215,0,0.25);}
    .combo-cell.is-lit.is-critical{background:rgba(255,140,0,0.2);color:rgba(255,180,0,0.85);border-color:rgba(255,160,0,0.4);}
    .combo-cell.is-hot{background:rgba(255,215,0,0.22);color:#FFD700;border-color:rgba(255,215,0,0.6);
      font-size:16px;animation:comboHop 0.5s cubic-bezier(0.2,1.4,0.35,1) both;
      box-shadow:0 0 10px rgba(255,215,0,0.5);}
    .combo-cell.is-hot.is-critical{background:rgba(255,140,0,0.3);color:#FFA500;border-color:rgba(255,160,0,0.7);
      box-shadow:0 0 14px rgba(255,140,0,0.7);}
    .combo-max-label{font-size:10px;font-weight:900;color:#FF8C00;padding:2px 5px;border-radius:4px;
      background:rgba(255,140,0,0.15);animation:pulse 0.8s ease-in-out infinite alternate;}
    .combo-best{font-family:'Share Tech Mono',monospace;font-size:11px;font-weight:900;color:#F5A623;
      padding:3px 7px;border-radius:8px;background:rgba(245,166,35,0.12);white-space:nowrap;}
    .seg-settle{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2400;
      pointer-events:none;text-align:center;animation:segSettleIn 0.3s cubic-bezier(0.2,1.4,0.35,1) both;}
    .seg-settle-card{min-width:180px;padding:14px 20px;border-radius:16px;
      background:rgba(10,22,58,0.96);border:1px solid rgba(255,255,255,0.1);
      box-shadow:0 8px 32px rgba(0,0,0,0.6);}
    .seg-settle.won .seg-settle-card{border-color:rgba(255,215,0,0.35);box-shadow:0 8px 32px rgba(0,0,0,0.6),0 0 24px rgba(255,215,0,0.15);}
    .seg-settle.lost .seg-settle-card{border-color:rgba(232,69,60,0.35);box-shadow:0 8px 32px rgba(0,0,0,0.6),0 0 24px rgba(232,69,60,0.1);}
    .seg-settle-pnl{font-family:'Share Tech Mono',monospace;font-size:32px;font-weight:900;line-height:1;}
    .seg-settle.won .seg-settle-pnl{color:#FFD700;}
    .seg-settle.lost .seg-settle-pnl{color:#FF3B30;}
    .seg-settle-bonus{font-size:13px;font-weight:700;color:#FFA500;margin-top:4px;}
    .seg-settle-streak{font-size:12px;color:#8BA8D4;margin-top:6px;}
    .seg-settle-reset{font-size:13px;font-weight:700;color:#FF3B30;margin-top:4px;}
    .first-tip{position:fixed;bottom:140px;left:50%;transform:translateX(-50%);z-index:2500;
      pointer-events:none;white-space:nowrap;
      background:rgba(10,22,58,0.95);border:1px solid rgba(46,127,232,0.4);
      border-radius:12px;padding:10px 18px;font-size:13px;color:#8BA8D4;font-weight:600;
      box-shadow:0 4px 20px rgba(0,0,0,0.5);animation:tipFadeIn 0.4s ease both,tipFadeOut 0.4s ease 3.6s both;}
    .first-tip b{color:#FFD700;}
    @keyframes segSettleIn{from{opacity:0;transform:translate(-50%,-50%) scale(0.7);}to{opacity:1;transform:translate(-50%,-50%) scale(1);}}
    @keyframes tipFadeIn{from{opacity:0;transform:translateX(-50%) translateY(8px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}
    @keyframes tipFadeOut{from{opacity:1;}to{opacity:0;}}
    @keyframes pulse{from{opacity:0.7;}to{opacity:1;}}
    .combo-burst{position:fixed;top:49%;left:50%;z-index:2300;pointer-events:none;text-align:center;
      transform:translate(-50%,-50%);animation:comboBurst 0.78s cubic-bezier(0.14,1.15,0.28,1) both;}
    .combo-burst-main{font-family:'Share Tech Mono',monospace;font-size:72px;line-height:0.9;font-style:italic;
      font-weight:900;color:#FFE66B;text-shadow:0 0 12px rgba(255,230,107,0.95),0 0 36px rgba(255,128,0,0.7),0 5px 0 rgba(91,31,0,0.75);}
    .combo-burst-sub{margin-top:8px;font-family:'Share Tech Mono',monospace;font-size:20px;font-weight:900;color:${GOLD};
      text-shadow:0 0 18px ${GOLD}88;}
    .combo-burst.is-critical .combo-burst-main{font-size:88px;color:#FFF07A;animation:comboCriticalGlow 0.22s ease-in-out 3 alternate;}
    .g-bottom{flex-shrink:0;padding:12px 20px 32px;}
    .g-jackpot{display:flex;gap:10px;margin-bottom:12px;}
    .g-jcell{flex:1;background:#0F2554;border-radius:12px;padding:10px 12px;text-align:center;border:1px solid rgba(46,127,232,0.3);}
    .g-jlabel{font-size:9px;color:#8BA8D4;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:3px;}
    .g-jval{font-size:16px;font-weight:800;font-family:'Share Tech Mono',monospace;}
    .g-trade-btns{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
    .g-tbtn{height:64px;border-radius:12px;border:none;font-size:20px;font-weight:900;
      cursor:pointer;transition:all 0.12s;font-family:'Inter',sans-serif;
      -webkit-tap-highlight-color:transparent;display:flex;flex-direction:column;
      align-items:center;justify-content:center;gap:1px;position:relative;overflow:hidden;}
    .g-tbtn span{font-size:11px;font-weight:500;opacity:0.75;}
    .g-tbtn:active{transform:scale(0.96)!important;}
    .g-tbtn:disabled{opacity:0.3;cursor:not-allowed;}
    .g-tbtn.is-pending:disabled{opacity:1;}
    .g-tbtn:after{content:'';position:absolute;left:50%;top:50%;width:0;height:0;border-radius:999px;
      background:rgba(255,255,255,0.34);transform:translate(-50%,-50%);opacity:0;pointer-events:none;}
    .g-tbtn:active:after{animation:tradeRipple 0.42s ease-out;}
    .g-tbtn.is-pending{animation:btnPending 0.55s ease-in-out infinite alternate;}
    .g-pts-bar{padding:0 20px 14px;flex-shrink:0;}
    .g-pts-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;}
    .g-pts-label{font-size:12px;font-weight:700;color:#ffffff;}
    .g-pts-val{font-family:"Share Tech Mono",monospace;font-size:14px;font-weight:700;}
    .g-pts-track-wrap{position:relative;padding-top:14px;}
    .g-pts-indicator{position:absolute;top:0;transform:translateX(-50%);
      width:0;height:0;
      border-left:7px solid transparent;
      border-right:7px solid transparent;
      border-top:10px solid #F5C518;
      filter:drop-shadow(0 0 4px rgba(245,197,24,0.8));
      transition:left 0.3s;}
    .g-pts-track{height:14px;background:#091840;border-radius:8px;overflow:hidden;
      box-shadow:inset 0 2px 6px rgba(0,0,0,0.9),inset 0 -1px 0 rgba(255,255,255,0.05);}
    .g-pts-fill{height:100%;border-radius:8px;transition:width 0.3s;
      background:linear-gradient(180deg,#FFD700 0%,#E6A800 100%) !important;
      box-shadow:inset 0 2px 0 rgba(255,255,255,0.35),0 0 12px rgba(245,197,24,0.5);}
    .g-wait-next{flex:1;display:flex;flex-direction:column;justify-content:center;gap:16px;
      padding:0 20px 32px;}
    .g-wait-next-title{font-size:26px;font-weight:900;color:#fff;line-height:1.1;}
    .g-wait-next-sub{font-size:13px;color:#8BA8D4;line-height:1.6;}
    .g-wait-next-panel{background:#0F2554;border:1px solid rgba(46,127,232,0.3);border-radius:16px;
      padding:16px;display:grid;gap:12px;box-shadow:0 18px 42px rgba(0,0,0,0.32);}
    .g-wait-next-row{display:flex;align-items:center;justify-content:space-between;gap:16px;}
    .g-wait-next-label{font-size:12px;color:#555;}
    .g-wait-next-value{font-size:14px;color:#fff;font-weight:800;text-align:right;}
    .g-wait-panel{flex:1;display:flex;flex-direction:column;align-items:center;
      justify-content:center;gap:16px;padding:20px;}
    .g-ready-btn{width:100%;height:64px;border-radius:12px;border:none;font-size:18px;font-weight:900;
      cursor:pointer;font-family:'Inter',sans-serif;transition:all 0.15s;
      background:linear-gradient(180deg,#FFD700 0%,#E6A800 100%);
      color:#000;box-shadow:0 4px 0 #8B6200,0 6px 12px rgba(0,0,0,0.4);}
    .g-ready-btn:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .g-cd-num{font-size:96px;font-weight:900;color:#ffffff;line-height:1;
      animation:cdPulse 1s ease-in-out infinite;}
    @keyframes cdPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.7;transform:scale(0.95)}}
    .bullet-layer{position:fixed;top:100px;left:50%;transform:translateX(-50%);
      z-index:500;width:88%;max-width:380px;pointer-events:none;}
    .bullet{padding:7px 16px;border-radius:50px;margin-bottom:5px;
      font-size:12px;font-weight:600;text-align:center;
      animation:bIn 0.2s ease;}
    .flash-pop{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
      z-index:2000;border-radius:16px;padding:18px 40px;text-align:center;
      animation:flashPop 0.18s ease;pointer-events:none;}
    .trade-notice-layer{position:fixed;top:86px;left:50%;transform:translateX(-50%);
      z-index:1600;width:88%;max-width:380px;pointer-events:none;}
    .trade-notice{padding:6px 14px;border-radius:99px;background:transparent;
      border:none;font-size:14px;font-weight:800;
      text-align:center;white-space:nowrap;animation:noticeIn 0.22s ease;color:#ffffff !important;}
    .trade-notice.buy,.trade-notice.profit{color:#ffffff;}
    .trade-notice.sell,.trade-notice.loss{color:#ffffff;}
    @keyframes bIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
    @keyframes noticeIn{from{opacity:0;transform:translateY(-12px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
    @keyframes flashPop{from{opacity:0;transform:translate(-50%,-50%) scale(0.86)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}
    @keyframes comboHop{0%{transform:translateY(8px) skew(-8deg) scale(0.86)}58%{transform:translateY(-5px) skew(-8deg) scale(1.2)}100%{transform:translateY(0) skew(-8deg) scale(1)}}
    @keyframes comboBurst{0%{opacity:0;transform:translate(-50%,-45%) scale(0.58) rotate(-7deg)}
      16%{opacity:1;transform:translate(-50%,-50%) scale(1.22) rotate(3deg)}
      48%{opacity:1;transform:translate(-50%,-50%) scale(1) rotate(0)}
      100%{opacity:0;transform:translate(-50%,-62%) scale(0.92) rotate(0)}}
    @keyframes comboCriticalGlow{from{filter:brightness(1)}to{filter:brightness(1.45)}}
    @keyframes tradeRipple{from{width:0;height:0;opacity:0.45}to{width:220px;height:220px;opacity:0}}
    @keyframes btnPending{from{filter:brightness(1)}to{filter:brightness(1.22)}}
    @keyframes gFloatPulse{from{transform:scale(0.96)}to{transform:scale(1)}}
    @keyframes scoreTick{from{opacity:0.74;transform:translateY(5px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
    @keyframes scoreShine{0%,45%{opacity:0;transform:translateX(-50%) rotate(18deg)}70%{opacity:1}100%{opacity:0;transform:translateX(220%) rotate(18deg)}}
    @keyframes scoreDanger{from{filter:brightness(1)}to{filter:brightness(1.25)}}
    @keyframes priceFlipOld{from{opacity:1;transform:rotateX(0deg) translateY(0)}
      to{opacity:0;transform:rotateX(-84deg) translateY(-8%)}}
    @keyframes priceFlipNew{from{opacity:0;transform:rotateX(84deg) translateY(8%)}
      to{opacity:1;transform:rotateX(0deg) translateY(0)}}
    @keyframes gPulse{0%,100%{text-shadow:0 0 20px ${GOLD}66}50%{text-shadow:0 0 40px ${GOLD}cc}}
    @media(min-width:768px){.g-price-big{font-size:44px;}.g-live-score-value{font-size:42px;}.g-tbtn{height:72px;font-size:24px;}.combo-burst-main{font-size:94px;}.combo-burst.is-critical .combo-burst-main{font-size:112px;}}
  `;

  /* ── 特殊状态 ── */
  if(kicked) return (
    <div style={{height:'100vh',background:'#0B1E4A',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12}}>
      <style>{S}</style>
      <div style={{fontSize:24,fontWeight:700,color:RED}}>{kicked}</div>
      <div style={{color:'#ffffff',fontSize:13}}>{lang==='en'?'Returning to lobby in 3s...':'3秒后返回大厅...'}</div>
    </div>
  );

  if(gameState==='WAIT_NEXT'){
    const busyLevel = busyRoom?.level || Number(level);
    const busyCount = busyRoom ? `${busyRoom.playerCount || 0}/${busyRoom.maxPlayers || '-'}` : '--';
    return (
      <div className="g-root">
        <style>{S}</style>
        <div className="g-header">
          <button className="g-back" onClick={goLobby}>&#8592;</button>
          <div className="g-title">{getRoomLabel(level)}</div>
          <div style={{width:36}}/>
        </div>
        <div className="g-wait-next">
          <div>
            <div className="g-wait-next-title">{lang==='en'?'Next Round':'等待下一局'}</div>
            <div className="g-wait-next-sub">{lang==='en'?'This room is busy. Please wait a moment.':'当前等级房间暂不可加入，请稍后再进入。'}</div>
          </div>
          <div className="g-wait-next-panel">
            <div className="g-wait-next-row">
              <span className="g-wait-next-label">{lang==='en'?'Room':'房间等级'}</span>
              <span className="g-wait-next-value">{getRoomLabel(busyLevel)}</span>
            </div>
            <div className="g-wait-next-row">
              <span className="g-wait-next-label">{lang==='en'?'Status':'房间状态'}</span>
              <span className="g-wait-next-value" style={{color:GOLD}}>{getStatusLabel(busyRoom?.status)}</span>
            </div>
            <div className="g-wait-next-row">
              <span className="g-wait-next-label">{lang==='en'?'Players':'当前人数'}</span>
              <span className="g-wait-next-value">{busyCount}</span>
            </div>
          </div>
          <button className="g-ready-btn" style={{background:GOLD,color:'#000'}} onClick={goLobby}>
            {t('returnLobby')}
          </button>
        </div>
      </div>
    );
  }

  if(gameState==='ENDED'&&settled){
    const won=(settled.settlePts||0)>=carryPts.current;
    const netPts = (settled.settlePts||0) - carryPts.current;
    const netStr = (netPts>=0?'+':'')+netPts.toLocaleString();
    return (
      <SettleScreen
        won={won} netStr={netStr} settled={settled}
        carryPts={carryPts.current} LIME={LIME} RED={RED} S={S}
        onRetry={goRetry} onLobby={goLobby}
        lang={lang} t={t}
      />
    );
  }

  /* ── 主界面 ── */
  return (
    <div className="g-root">
      <style>{S}{`
        @keyframes danger-breathe {
          0%   { opacity: 0.0; }
          50%  { opacity: 0.55; }
          100% { opacity: 0.0; }
        }
        .danger-vignette {
          position: fixed; inset: 0; pointer-events: none; z-index: 999;
          animation: danger-breathe 1s ease-in-out infinite;
          background: radial-gradient(ellipse at center,
            transparent 30%,
            rgba(232,69,60,0.35) 65%,
            rgba(232,69,60,0.75) 100%
          );
        }
      `}</style>

      {/* 危险模式：呼吸红晕 */}
      {dangerMode && gameState === 'RUNNING' && <div className="danger-vignette"/>}

      {topNotice && (
        <div className="trade-notice-layer">
          <div key={topNotice.id} className={`trade-notice ${topNotice.type}`}>{topNotice.text}</div>
        </div>
      )}

      {errMsg && (
        <div className="trade-notice-layer" style={{top:topNotice ? 132 : 86}}>
          <div className="trade-notice loss">{errMsg}</div>
        </div>
      )}

      {/* 弹幕 */}
      <div className="bullet-layer">
        {bullets.map(b=>(
          <div key={b.id} className="bullet" style={{
            background:b.type==='bust'?'rgba(232,69,60,0.9)':b.type==='warn'?'rgba(245,166,35,0.9)':'rgba(50,50,50,0.95)',
            color:'#fff',
          }}>{b.text}</div>
        ))}
      </div>

      {/* PnL 浮层 */}
      {flashMsg && !segSettle && (
        <div className="flash-pop" style={{
          background:'transparent',border:'none',
          boxShadow:`0 0 40px ${flashMsg.color}44`,
        }}>
          <div style={{fontFamily:'Share Tech Mono',fontSize:48,fontWeight:900,color:flashMsg.color,whiteSpace:'nowrap'}}>
            {flashMsg.text} COIN
          </div>
        </div>
      )}

      {comboPop && !segSettle && (
        <div key={comboPop.id} className={`combo-burst ${comboPop.multiplier>=3?'is-critical':''}`}>
          <div className="combo-burst-main">x{comboPop.multiplier}</div>
          {comboPop.bonus>0 && (
            <div className="combo-burst-sub">+{Math.round(comboPop.bonus).toLocaleString()} COIN</div>
          )}
        </div>
      )}

      {/* 切换结算卡片 */}
      {segSettle && (
        <div className={`seg-settle ${segSettle.won ? 'won' : 'lost'}`}>
          <div className="seg-settle-card">
            <div className="seg-settle-pnl">{segSettle.pnl >= 0 ? '+' : ''}{segSettle.pnl.toLocaleString()}</div>
            {segSettle.won && segSettle.bonus > 0 && (
              <div className="seg-settle-bonus">{t('streakBonus')} +{segSettle.bonus.toLocaleString()}</div>
            )}
            {segSettle.won && (
              <div className="seg-settle-streak">{lang==='en'?`Streak x${segSettle.streak} — keep going`:`连胜 x${segSettle.streak} — 继续保持不要切换`}</div>
            )}
            {!segSettle.won && (
              <div className="seg-settle-reset">{t('segReset')}</div>
            )}
          </div>
        </div>
      )}

      {/* 首次游戏提示 */}
      {firstTip && (
        <div className="first-tip">
          {lang==='en' ? <>Switch direction after profit to chain streaks, <b>up to x5 crit bonus</b></> : <>盈利后切换方向可叠加连胜，<b>最高 x5 暴击奖励</b></>}
        </div>
      )}

      {/* 顶部 header */}
      <div className="g-header">
        <button className="g-back" onClick={goLobby}>&#8592;</button>
        <div className="g-title">
          $ANSEM / USDT
        </div>
        <div style={{width:36,textAlign:'right',display:'flex',alignItems:'center',justifyContent:'flex-end'}}>
          <LangToggle />
        </div>
      </div>

      {/* 跑马灯公告 */}
      <Marquee text={notice.text} url={notice.url} />

      {/* 价格大字 */}
      <div className="g-price-hero">
        <FlipPrice value={heroPrice} />
        <div className="g-price-sub">
          {direction!=null && openPrice!=null ? (
            <>
              <span key={floatingPtsText} className="g-float-chip" style={{
                background:floatingIsProfit?'rgba(198,241,53,0.12)':'rgba(232,69,60,0.12)',
                color:floatingIsProfit?GOLD:RED,
                boxShadow:floatingIsProfit?`0 0 20px ${GOLD}22`:`0 0 20px ${RED}22`,
              }}>
                {lang==='en'?(floatingIsProfit?'PnL':'PnL'):(floatingIsProfit?'浮盈':'浮亏')} {floatingPtsText} pts
              </span>
              <span style={{padding:'3px 10px',borderRadius:50,fontSize:12,fontWeight:700,
                background:isProfit?'rgba(198,241,53,0.08)':'rgba(232,69,60,0.08)',
                color:isProfit?GOLD:RED}}>
                {isProfit?'+':''}{pnlPct}%
              </span>
              <span style={{fontSize:12,color:'#ffffff'}}>{lang==='en'?'Entry':'建仓'} ${openPrice.toFixed(4)}</span>
            </>
          ) : (
            <span style={{fontSize:15,color:'#ffffff',fontWeight:700}}>{lang==='en'?roomCfg.nameEn:roomCfg.name} · {playerCount} {lang==='en'?'players':'人'}</span>
          )}
          {gameState==='RUNNING' && bustPool>0 && (
            <span style={{marginLeft:'auto',fontSize:12,color:'#F5A623',fontWeight:600}}>
              {lang==='en'?'Pool':'奖池'} {bustPool.toLocaleString()}
            </span>
          )}
        </div>
        {(gameState==='RUNNING'||gameState==='BUST') && currentPts!=null && (
          <div className={`g-live-score ${currentPtsClass}`}>
            <div className="g-live-score-top">
              <span className="g-live-score-label">{lang==='en'?'Round PTS':'局内 PTS'}</span>
              {direction!=null && openPrice!=null && (
                <span className="g-live-score-delta">
                  {lang==='en'?(floatingIsProfit?'+':'-'):(floatingIsProfit?'浮盈':'浮亏')} {floatingPtsText}
                </span>
              )}
            </div>
            <div key={Math.round(currentPts)} className="g-live-score-value">
              {isBust ? '0' : Math.max(0, Math.round(currentPts)).toLocaleString()} PTS
            </div>
            <div className="g-live-score-meter">
              <div className="g-live-score-fill" style={{
                width: isBust ? '0%' : `${currentPtsRatio}%`,
                color: (isBust||currentPtsClass==='is-danger'||currentPtsClass==='is-loss') ? RED : GOLD,
                background: (isBust||currentPtsClass==='is-danger'||currentPtsClass==='is-loss') ? RED : GOLD,
              }}/>
            </div>
          </div>
        )}
        {gameState==='RUNNING' && <ComboMeter streak={comboInfo.streak} best={comboInfo.best} lang={lang} />}
      </div>

      {/* K线图区域 */}
      <div className="g-chart-area" style={{position:'relative'}}>
        {(gameState==='WAITING'||gameState==='READYING') && (playerCount*(roomCfg?.carry||0))>0 && (
          <div style={{
            position:'absolute',top:8,right:8,zIndex:10,
            background:'rgba(11,30,74,0.85)',borderRadius:8,
            padding:'5px 10px',textAlign:'right',lineHeight:1.5,
            border:'1px solid rgba(245,197,24,0.25)',
          }}>
            <div style={{fontSize:10,color:'#8BA8D4'}}>{t('bustPool')}</div>
            <div style={{color:GOLD,fontFamily:'Share Tech Mono',fontWeight:700,fontSize:13}}>
              {(playerCount*(roomCfg?.carry||0)).toLocaleString()} COIN
            </div>
          </div>
        )}
        {pricePoints.length > 0
          ? <CandleChart points={pricePoints} direction={direction} markers={tradeLogs} />
          : <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:'#ffffff',fontSize:14}}>
              加载行情数据...
            </div>
        }
        {gameState==='COUNTDOWN' && countdown!=null && (
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',
            justifyContent:'center',pointerEvents:'none',zIndex:10}}>
            <div style={{fontFamily:'Share Tech Mono',fontSize:'30vw',fontWeight:900,
              color:'#ffffff',opacity:0.3,lineHeight:1,userSelect:'none'}}>
              {countdown}
            </div>
          </div>
        )}
        {gameState==='RUNNING' && remain!=null && (
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',
            justifyContent:'center',pointerEvents:'none',zIndex:10}}>
            <div style={{fontFamily:'Share Tech Mono',fontSize:'30vw',fontWeight:900,
              color:'#ffffff',opacity:0.3,lineHeight:1,userSelect:'none'}}>
              {String(remain).padStart(2,'0')}
            </div>
          </div>
        )}
      </div>

      {/* 底部操作区 */}
      <div className="g-bottom">



        {/* 等待/准备 */}
        {(gameState==='WAITING'||gameState==='READYING') && (
          <div style={{textAlign:'center',marginBottom:16,position:'relative'}}>
            <div style={{fontSize:15,color:'#ffffff',fontWeight:600,marginBottom:16}}>
              {gameState==='WAITING'
                ? (lang==='en'?`${playerCount} player${playerCount!==1?'s':''} waiting — need 2+ to start`:`目前有 ${playerCount} 人等待，满足 2 人以上即可开始`)
                : (lang==='en'?'Waiting for all players to ready up...':'等待全员准备...')}
            </div>
            <button className="g-ready-btn"
              style={isReady || readyPending
                ? {background:'#152D6A',color:'#4A6A9A',boxShadow:'none',transform:'none'}
                : pricePoints.length === 0
                  ? {opacity:0.4,cursor:'not-allowed'}
                  : {}}
              onClick={sendReady} disabled={isReady || readyPending || pricePoints.length === 0}>
              {isReady
                ? t('alreadyReady')
                : readyPending
                  ? (lang==='en'?'Submitting...':'准备提交中...')
                  : pricePoints.length === 0 ? t('loadingMarket') : t('readyBtn')}
            </button>
          </div>
        )}

        {/* 倒计时提示文字 */}
        {gameState==='COUNTDOWN' && (
          <div style={{textAlign:'center',marginBottom:8}}>
            <div style={{fontSize:15,color:'#ffffff',fontWeight:600}}>{t('gameStarting')}</div>
          </div>
        )}

        {gameState==='SETTLING' && (
          <div style={{textAlign:'center',marginBottom:16,color:'#ffffff',fontWeight:700}}>
            {lang==='en'?'Settling this round...':'本局结算中，请稍候...'}
          </div>
        )}

        {/* 做多/做空按钮 */}
        {(gameState==='RUNNING'||gameState==='COUNTDOWN') && !isBust && (
          <div className="g-trade-btns">
            <button className={`g-tbtn ${switching&&pendingDir===-1?'is-pending':''}`}
              onClick={()=>switchDir(-1)} disabled={isBust||switching}
              style={{
                background:'linear-gradient(180deg,#F05349 0%,#C0312A 100%)',
                color:'#fff', border:'none',
                boxShadow: activeDir===-1
                  ? '0 4px 0 #7A1A16,0 6px 12px rgba(0,0,0,0.4)'
                  : '0 2px 0 #7A1A16',
                opacity: activeDir===1 ? 0.55 : 1,
                transform: activeDir===-1 ? 'translateY(0)' : 'translateY(2px)',
              }}>
              <span style={{fontSize:20,fontWeight:900,opacity:1}}>{switching&&pendingDir===-1?t('submitting'):activeDir===-1?t('shortActive'):activeDir===1?t('switchToShort'):t('shortDown')}</span>
              <span style={{fontSize:11,opacity:0.6,fontWeight:500}}>SELL</span>
            </button>
            <button className={`g-tbtn ${switching&&pendingDir===1?'is-pending':''}`}
              onClick={()=>switchDir(1)} disabled={isBust||switching}
              style={{
                background:'linear-gradient(180deg,#36D97A 0%,#1FA855 100%)',
                color:'#fff', border:'none',
                boxShadow: activeDir===1
                  ? '0 4px 0 #0F6B30,0 6px 12px rgba(0,0,0,0.4)'
                  : '0 2px 0 #0F6B30',
                opacity: activeDir===-1 ? 0.55 : 1,
                transform: activeDir===1 ? 'translateY(0)' : 'translateY(2px)',
              }}>
              <span style={{fontSize:20,fontWeight:900,opacity:1}}>{switching&&pendingDir===1?t('submitting'):activeDir===1?t('longActive'):activeDir===-1?t('switchToLong'):t('longUp')}</span>
              <span style={{fontSize:11,opacity:0.6,fontWeight:500}}>BUY</span>
            </button>
          </div>
        )}

        {/* 爆仓状态 */}
        {isBust && gameState!=='ENDED' && (
          <div style={{textAlign:'center',padding:'16px 0'}}>
            <div style={{fontSize:28,fontWeight:900,color:'#FF3B30',marginBottom:6}}>BUST</div>
            <div style={{fontSize:13,color:'#ffffff'}}>{t('bustWaiting')}</div>
          </div>
        )}

      </div>
    </div>
  );
}
